import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "../contexts/LangContext";
import { ArrowUpRight, HeartPulse, Moon, Brain, Sparkles, Timer, Check } from "lucide-react";

/**
 * Stressabbau — SEO Landing Page
 *
 * Zielt "stressabbau ayurveda" (~3'600 Suchanfragen/Monat, kommerzielle Absicht).
 * Kein neuer Service in der DB — nutzt die 3 bestehenden Behandlungen die
 * gegen Stress wirken (Abhyanga, Shiroabhyanga, Klangschale) und leitet ins
 * bestehende Buchungsformular.
 */

const COPY = {
  de: {
    eyebrow: "— Ayurveda gegen Stress",
    h1: "Innere Ruhe wiederfinden.",
    lead:
      "Wenn Anspannung, unruhiger Schlaf und ein zu voller Kopf zum Alltag geworden sind, wirkt Ayurveda dort, wo westliche Massnahmen oft nicht hinreichen: an der Wurzel. In unserer Praxis für Frauen in Sarmenstorf und Rupperswil kombinieren wir warme Ölmassagen, Klangschalen und Ernährungswissen zu einem Programm, das nachhaltig entspannt.",
    causesTitle: "Wenn Vata und Pitta aus dem Gleichgewicht geraten",
    causes:
      "Chronischer Stress ist im Ayurveda kein Zufall, sondern das Symptom eines gestörten Gleichgewichts. Vor allem die Doshas Vata (Bewegung, Nerven) und Pitta (Feuer, Ehrgeiz) reagieren auf modernen Alltag mit Unruhe, Reizbarkeit, Schlafproblemen und Erschöpfung. Ayurveda begegnet dem nicht mit Symptomunterdrückung, sondern mit gezielter Beruhigung durch Wärme, Berührung, Klang und Rhythmus.",
    signsTitle: "Erkennst du dich wieder?",
    signs: [
      "Du liegst nachts wach oder wachst um 3 Uhr auf und findest nicht mehr in den Schlaf",
      "Der Kopf ist voll – auch wenn eigentlich alles erledigt ist",
      "Verspannungen in Nacken, Schultern und Kiefer",
      "Reizbarkeit, Ungeduld, das Gefühl «nichts reicht mehr»",
      "Kalte Hände und Füsse, unruhige Verdauung, Zykluschaos",
    ],
    treatmentsTitle: "Drei Behandlungen für tiefe Entspannung",
    treatments: [
      {
        icon: Sparkles,
        name: "Abhyanga – Ganzkörper-Ölmassage",
        body:
          "Die Königin der ayurvedischen Massagen. Warmes, konstitutionsgerechtes Öl wird in langen, rhythmischen Bewegungen einmassiert. Das Nervensystem schaltet um, Stresshormone sinken messbar, tiefe Ruhe kehrt ein.",
        duration: "60 Min",
        price: "CHF 120",
        href: "/booking",
      },
      {
        icon: Brain,
        name: "Shiroabhyanga – Kopf- und Nackenmassage",
        body:
          "Fokussiert auf Kopf, Nacken und Schultern – dort, wo Stress körperlich zuerst sitzt. Löst mentale Anspannung, klärt den Geist und lindert stressbedingte Kopfschmerzen sowie Einschlafprobleme.",
        duration: "30 Min",
        price: "CHF 60",
        href: "/booking",
      },
      {
        icon: Moon,
        name: "Klangschalen-Ritual",
        body:
          "Bei jeder Massage inklusive: Zu Beginn und zum Abschluss beruhigen tibetische, indische und nepalesische Klangschalen Körper und Geist. Die Schwingungen wirken bis in die Zellen und erden auch danach noch tagelang.",
        duration: "In jeder Massage",
        price: "inklusive",
        href: "/klangschalen",
      },
    ],
    programTitle: "Empfohlenes Programm bei akutem Stress",
    programBody:
      "Wenn du gerade wirklich am Rand bist, empfehlen wir 3 Behandlungen im Abstand von je einer Woche. Studien zeigen, dass Ayurveda-Massagen den Cortisol-Spiegel (Stresshormon) messbar senken – erste Wirkung nach der ersten Sitzung, nachhaltige Veränderung ab der dritten. Schreib uns «PAKET» auf WhatsApp für einen Mehrfach-Rabatt (10–15 % ab 3 Terminen).",
    ctaBook: "Termin für Stressabbau buchen",
    ctaLearn: "Mehr über Klangschalen erfahren",
    faqTitle: "Häufige Fragen",
    faqs: [
      {
        q: "Wie schnell wirkt eine Ayurveda-Massage gegen Stress?",
        a: "Die meisten Kundinnen berichten schon während der Behandlung von tiefer Ruhe. Der spürbare Effekt hält 3-7 Tage an. Für nachhaltige Veränderung sind 3 Termine im Wochenrhythmus optimal.",
      },
      {
        q: "Ich habe noch nie Ayurveda gemacht – ist das das Richtige für mich?",
        a: "Absolut. Wir starten immer mit einem kurzen Vorgespräch, in dem wir deine aktuelle Situation und deinen Konstitutionstyp erfassen. Danach passen wir Öl, Druck und Ablauf an dich an.",
      },
      {
        q: "Wird das von der Krankenkasse übernommen?",
        a: "Nein. Ayurveda Aargau ist nicht krankenkassenanerkannt. Die Behandlungen sind Wellness- und Entspannungsleistungen. Wer ausdrücklich medizinische Therapie sucht, sollte zuerst ärztlichen Rat einholen.",
      },
      {
        q: "Nur für Frauen – warum?",
        a: "Damit wir gezielt auf frauenspezifische Themen eingehen können: Zyklus, Hormone, Wechseljahre, Belastungen aus Care-Arbeit. In unserer Praxis darf jede Frau einfach Frau sein.",
      },
    ],
  },
  en: {
    eyebrow: "— Ayurveda for Stress",
    h1: "Rediscover your inner calm.",
    lead:
      "When tension, restless sleep and a racing mind become your daily reality, Ayurveda works where Western approaches often can't – at the root. In our women-only practice in Sarmenstorf and Rupperswil we combine warm oil massage, singing bowls and nutritional wisdom into a programme that soothes lastingly.",
    causesTitle: "When Vata and Pitta lose their balance",
    causes:
      "In Ayurveda, chronic stress is a symptom of an imbalance – above all in the doshas Vata (movement, nerves) and Pitta (fire, ambition). Both react to modern life with restlessness, irritability, sleep problems and exhaustion. Ayurveda answers not with symptom suppression but with warmth, touch, sound and rhythm.",
    signsTitle: "Does this sound familiar?",
    signs: [
      "You lie awake at night or wake at 3 a.m. and can't fall back asleep",
      "Your head is full even when everything is technically done",
      "Tension in neck, shoulders and jaw",
      "Irritability, impatience, the feeling that nothing is ever enough",
      "Cold hands and feet, unsettled digestion, cycle chaos",
    ],
    treatmentsTitle: "Three treatments for deep relaxation",
    treatments: [
      {
        icon: Sparkles,
        name: "Abhyanga – Full-body oil massage",
        body:
          "The queen of Ayurveda treatments. Warm, constitution-matched oil in long rhythmic strokes. The nervous system shifts, stress hormones drop measurably, deep calm settles in.",
        duration: "60 min",
        price: "CHF 120",
        href: "/booking",
      },
      {
        icon: Brain,
        name: "Shiroabhyanga – Head and neck massage",
        body:
          "Focused on head, neck and shoulders – where stress lodges first. Releases mental tension, clears the mind, eases stress-related headaches and sleep issues.",
        duration: "30 min",
        price: "CHF 60",
        href: "/booking",
      },
      {
        icon: Moon,
        name: "Singing bowl ritual",
        body:
          "Included in every massage. Tibetan, Indian and Nepalese singing bowls calm body and mind at the start and end of your session. The vibrations reach cellular level and keep grounding you for days.",
        duration: "Every session",
        price: "included",
        href: "/klangschalen",
      },
    ],
    programTitle: "Recommended programme for acute stress",
    programBody:
      "If you're truly on the edge right now, we recommend 3 treatments one week apart. Studies show Ayurveda massages measurably lower cortisol – first effect after the first session, lasting change from the third. Send «PAKET» to us on WhatsApp for a multi-session discount (10-15 % from 3 sessions).",
    ctaBook: "Book stress-relief treatment",
    ctaLearn: "Learn more about singing bowls",
    faqTitle: "Frequent questions",
    faqs: [
      {
        q: "How fast does an Ayurveda massage work against stress?",
        a: "Most clients report deep calm already during the treatment. The effect lasts 3-7 days. For lasting change, 3 sessions at weekly intervals are optimal.",
      },
      {
        q: "I've never had Ayurveda – is this right for me?",
        a: "Absolutely. We always start with a short conversation about your situation and constitution. Oil, pressure and pace are then tailored to you.",
      },
      {
        q: "Is this covered by health insurance?",
        a: "No. Ayurveda Aargau is not recognised by Swiss health insurance. Treatments are wellness and relaxation services. For medical therapy please consult a doctor first.",
      },
      {
        q: "Women only – why?",
        a: "So we can focus on women-specific topics: cycle, hormones, menopause, care-work strain. In our practice every woman may simply be a woman.",
      },
    ],
  },
};

function trackStressCta(name) {
  try {
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", "generate_lead", { source: "stressabbau_page", item_id: name });
    }
  } catch (_) {}
}

export default function Stressabbau() {
  const { lang } = useLang();
  const c = COPY[lang] || COPY.de;

  return (
    <div data-testid="stressabbau-page">
      {/* Hero */}
      <section className="relative bg-sand-soft border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-32">
          <div className="max-w-3xl">
            <div className="text-xs uppercase tracking-[0.3em] text-terracotta mb-5">{c.eyebrow}</div>
            <h1 className="font-display font-light text-5xl lg:text-6xl text-ink leading-[1.05] tracking-tight">
              {c.h1}
            </h1>
            <p className="mt-8 text-lg text-ink-muted leading-relaxed max-w-2xl">{c.lead}</p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/booking"
                onClick={() => trackStressCta("hero_book")}
                className="inline-flex items-center gap-2 px-8 py-4 bg-terracotta text-white rounded-full hover:bg-terracotta-hover transition-colors font-medium"
                data-testid="stress-hero-book"
              >
                <HeartPulse className="w-4 h-4" strokeWidth={1.5} />
                {c.ctaBook}
                <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
              </Link>
              <Link
                to="/klangschalen"
                className="inline-flex items-center gap-2 px-8 py-4 border border-ink text-ink rounded-full hover:bg-ink hover:text-white transition-colors font-medium"
                data-testid="stress-hero-learn"
              >
                {c.ctaLearn}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Causes */}
      <section className="max-w-4xl mx-auto px-6 lg:px-12 py-20">
        <h2 className="font-display text-4xl text-ink leading-tight">{c.causesTitle}</h2>
        <p className="mt-6 text-ink-muted leading-relaxed">{c.causes}</p>

        <div className="mt-14 bg-white border border-border rounded-md p-8">
          <h3 className="font-display text-2xl text-ink mb-6">{c.signsTitle}</h3>
          <ul className="space-y-3">
            {c.signs.map((s, i) => (
              <li key={i} className="flex gap-3 text-ink">
                <span className="w-5 h-5 rounded-full bg-terracotta/10 text-terracotta flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check className="w-3 h-3" strokeWidth={2.5} />
                </span>
                <span className="text-sm leading-relaxed">{s}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Treatments */}
      <section className="bg-sand-soft border-y border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
          <h2 className="font-display text-4xl text-ink leading-tight max-w-3xl">{c.treatmentsTitle}</h2>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {c.treatments.map((t, i) => {
              const Icon = t.icon;
              return (
                <Link
                  key={i}
                  to={t.href}
                  onClick={() => trackStressCta(`treatment_${i}`)}
                  className="group bg-white border border-border rounded-md p-8 flex flex-col hover:-translate-y-1 hover:shadow-[0_18px_40px_-20px_rgba(43,31,22,0.35)] transition-all duration-300"
                  data-testid={`stress-treatment-${i}`}
                >
                  <div className="w-12 h-12 rounded-full bg-terracotta/10 text-terracotta flex items-center justify-center mb-6">
                    <Icon className="w-5 h-5" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-display text-2xl text-ink leading-snug mb-3">{t.name}</h3>
                  <p className="text-sm text-ink-muted leading-relaxed flex-1 mb-6">{t.body}</p>
                  <div className="flex items-center justify-between pt-5 border-t border-border/70 text-sm">
                    <span className="text-ink flex items-center gap-1.5">
                      <Timer className="w-3.5 h-3.5" strokeWidth={1.5} />
                      {t.duration}
                    </span>
                    <span className="font-display text-lg text-terracotta">{t.price}</span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Program */}
      <section className="max-w-4xl mx-auto px-6 lg:px-12 py-20">
        <h2 className="font-display text-4xl text-ink leading-tight">{c.programTitle}</h2>
        <p className="mt-6 text-ink-muted leading-relaxed">{c.programBody}</p>
        <div className="mt-10">
          <Link
            to="/booking"
            onClick={() => trackStressCta("program_book")}
            className="inline-flex items-center gap-2 px-8 py-4 bg-terracotta text-white rounded-full hover:bg-terracotta-hover transition-colors font-medium"
            data-testid="stress-program-book"
          >
            {c.ctaBook}
            <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
          </Link>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-sand-soft border-t border-border">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 py-20">
          <h2 className="font-display text-4xl text-ink leading-tight">{c.faqTitle}</h2>
          <div className="mt-10 space-y-6">
            {c.faqs.map((f, i) => (
              <div key={i} className="bg-white border border-border rounded-md p-6" data-testid={`stress-faq-${i}`}>
                <h3 className="font-display text-xl text-ink mb-2">{f.q}</h3>
                <p className="text-sm text-ink-muted leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
